import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { CreditAmountPicker } from './CreditAmountPicker';

interface CreditRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: 'signup' | 'insufficient_credits';
}

export function CreditRequiredModal({
  isOpen,
  onClose,
  context = 'insufficient_credits',
}: CreditRequiredModalProps) {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const [error, setError] = useState<string | null>(null);
  const isVerified = !!user?.is_admin || !!user?.email_verified;

  if (!isOpen) return null;

  const title = context === 'signup' ? 'Credits required to start' : 'Add credits to continue';
  const body = context === 'signup'
    ? 'Textborne uses paid AI inference for world generation and messages. Add credits before starting your first simulation.'
    : 'Your current balance is below the generation threshold. Add credits to keep sending messages.';

  const handleVerifyEmail = () => {
    onClose();
    navigate('/account');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-700 rounded-lg w-full max-w-lg shadow-2xl border border-primary-700/40 overflow-hidden">
        <div className="px-7 pt-7 pb-5 border-b border-dark-500">
          <p className="text-xs uppercase tracking-[0.2em] text-primary-300/80">
            Billing
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-dark-50">
            {title}
          </h1>
        </div>

        <div className="px-7 py-6 space-y-4">
          <p className="text-dark-100 leading-relaxed">
            {body}
          </p>
          {!isVerified && (
            <div className="rounded-lg border border-amber-700/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
              Verify your email before purchasing credits.
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-700 bg-red-900/40 px-4 py-3 text-sm text-red-100">
              {error}
            </div>
          )}

          {isVerified && (
            <div className="rounded-lg border border-dark-500 bg-dark-800 px-4 py-4">
              <CreditAmountPicker
                isVerified={isVerified}
                submitLabel="Add credits"
                onError={setError}
              />
            </div>
          )}
        </div>

        <div className="px-7 pb-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Not now
          </button>
          {!isVerified && (
            <button
              type="button"
              onClick={handleVerifyEmail}
              className="btn-primary flex items-center justify-center gap-2"
            >
              Verify Email
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
